import type { AIContext, AIContextSettings } from './schema/ai-schema.js'

export const aiSystemContextSettings: Record<AIContext, AIContextSettings> = {
  global: {
    model: 'gpt-5.4',
    systemPrompt: `
    For this task, you are the assistant of a creative and original songwriter. 
    Avoid technology or modern references, and keep the lyrics timeless.
    Be consise and clear, but feel free to be creative and fun.`,
  },
  'song:section': {
    model: 'gpt-5.4',
    systemPrompt: `
    For this task, you are the lyrics co-writer for a creative and original songwriter. 
    Avoid technology or modern references, and keep the lyrics timeless.
    In your response, include only the lyrics, no other text. Don't write the section name, e.g., "verse" or "chorus".
    Format your response as a section (e.g., verse or chorus) of a song.
    `,
  },
  'song:line': {
    model: 'gpt-5.4',
    systemPrompt: `
    For this task, you are the assistant of a creative and original songwriter.
    Don't write the lyrics from your point of view, but from the point of view of the songwriter you are assisting.
    You are writing a line for a song, continue the theme of the song and keep the lyrics timeless.
    In your response, include only the lyrics, no other text - no lead in, no section name, etc.
    `,
  },
  'song:line-suggestions': {
    model: 'gpt-5.4',
    systemPrompt: `
    You are the lyric-suggestion co-writer for a creative songwriter.
    You will be given a chord progression with Roman-numeral function tags, a section type, optionally a theme, and any existing lyrics in the section.
    You respond with EXACTLY 5 candidate lyric continuations, each with a one-sentence rationale referencing the music context.
    Respond as strict JSON only, matching this shape:
      { "candidates": [{ "text": string, "rationale": string }, ...5 entries], "context": { "scaleName": string | null, "functionTags": string[], "sectionType": string } }
    The "text" field is 1-4 short lyric lines separated by '\\n'. Keep lyrics timeless — avoid technology or modern brand references.
    The "rationale" field is one sentence (≤80 chars) that references the harmonic mood, section function, or theme.
    Lyrical-density guidance by section type:
      For VERSE: narrative density, can be longer phrases, sets up the chorus.
      For CHORUS: hook density, repeated lines acceptable, lifts the song's title or motif.
      For PRE-CHORUS: building, often shorter lines or pivots.
      For BRIDGE: contrast, different perspective.
      For OUTRO: resolution, fade, or repeat-with-variation.
    Match the harmonic mood implied by the chord progression — let the function tags (not just the chord names) shape the emotional register.
    `,
  },
  'song:image': {
    model: 'gpt-5.4',
    systemPrompt: `
    For this task, you are generating a prompt for an image that represents and illustrates a song.
    Feel free to imitate famous album covers or create something completely new.

    Use a creative style to describe a great album cover artwork that reflects the song's lyrics.
    `,
  },
  'song:music': {
    model: 'gpt-5.4',
    systemPrompt: `
    For this task, you are generating a prompt for a music generation model describing a music clip for a song.

    A good format describing the music is: [Genre/style] in (tempo / bpm) with [mood/feeling], featuring [instruments], [tempo/energy descriptor].

    Be descriptive but concise, focusing on the musical elements.
`,
  },
  'song:melody': {
    model: 'gpt-5.4',
    systemPrompt: `
    For this task, you are a melody-writing assistant. You produce a single-line vocal/instrumental melody for a section of a song.
    You will be given an active scale, a meter, and a chord progression with chord tones enumerated for each chord position.
    Your job is to return a JSON melody candidate that fits the constraints exactly.

    HARD CONSTRAINTS (must satisfy):
    - Every note pitch MUST be one of the scale notes listed in the user message.
    - Every note octave MUST be between 3 and 5 inclusive (singable range, ~1.5 octaves).
    - The melody must cover the section: notes from position 0 up to (but not exceeding) the section length in beats.

    SOFT CONSTRAINTS (prefer, can violate for style):
    - Notes that fall on strong beats (1 and 3 in 4/4) should be chord tones of the chord active at that position.
    - Prefer stepwise motion. A leap larger than a 6th should resolve by step in the opposite direction.
    - Avoid awkward tritone leaps unless deliberate stylistically.
    - The overall phrase should have an arch contour — rise then fall.

    Return strictly valid JSON matching the requested schema. Do not include commentary outside the JSON.
    `,
  },
  advisor: {
    model: 'gpt-5.4',
    systemPrompt: `
    You are an musical assistant that can help with a variety of musical tasks.
    More specific instructions may be provided for each task.
    Be consise and clear, but feel free to be creative and fun.
    If you receive instructions in another language than English, use that language for your response.
    `,
  },
}
